import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { LifecycleBar } from "@/components/repo-lifecycle/LifecycleBar";
import { LIVE_REF, apiRef, repoErrorMessage, writeBlockedReason } from "@/components/repo-lifecycle/lifecycleVocab";
import { useManagedRepos, useRepoFile } from "@/api/queries/gitContent";
import { useSaveRepoFile } from "@/api/mutations/gitContent";
import { useConnectors } from "@/api/queries/connectors";
import { ApiError } from "@/api/client";
import { darfJetzt, darfVorgang, useUserContext } from "@/auth/operations";
import type { ProcessDefinitionView } from "@/types";
import {
  baueAbschnitte,
  darfSpeichern,
  hatFehler,
  leseEntwurf,
  leseKontext,
  leseSpecDokument,
  pruefeEntwurf,
  serialisiereSpec,
  type Befund,
  type SpecEntwurf,
} from "./specDocument";
import {
  AusloeserMaske,
  EingabenMaske,
  InstanziierungsMaske,
  ReglerMaske,
  SystemeMaske,
  WeisungenMaske,
} from "./specMasks";

// #326 (UC-1): die sechs Abschnitts-Masken am Prozess.
//
// Bis hierher war die Definition nur als roher JSON-Text zu pflegen (Werkbank). Diese Karte
// gibt den sechs Abschnitten, die eine Vorlage von einer anderen unterscheiden, je eine
// Maske — im Karten-Muster der übrigen Prozess-Oberfläche, ohne dass jemand die Feldnamen
// des Schemas kennen muss.
//
// DREI Dinge, die dabei nicht schiefgehen dürfen:
//
//  1. **Verlustfreiheit.** Was die Masken nicht kennen, kommt unverändert wieder heraus —
//     dafür sorgt `specDocument` (patch-over-raw). Die Karte serialisiert NIE aus typisierten
//     Objekten.
//  2. **Der Live-Stand bleibt unberührt.** Die Ablage eines Prozesses ist freigabepflichtig:
//     geschrieben wird ausschliesslich in einen Entwurf, und die Karte sagt das VORHER,
//     statt jemanden in das 403 des Masters laufen zu lassen.
//  3. **Der Master bleibt der Richter.** Die Masken verhindern, was er ablehnen würde
//     (`pruefeEntwurf`), aber sie behaupten nie, recht zu haben: seine 422-Fundstellen
//     werden mit Feldpfad angezeigt, nicht in ein „Speichern fehlgeschlagen" verwandelt.

const SPEC_PFAD = "process.json";

interface Stand {
  /**
   * ZU WELCHEM Stand gehört das Gelesene? Ohne diese Angabe blieben beim Umschalten die
   * Masken des vorigen Standes bedienbar, bis die neue Datei eintrifft — und jede Eingabe
   * in diesem Moment wäre still verloren (der Nachlade-Effekt setzt den Entwurf frisch).
   */
  ref: string;
  sha: string;
  /** Das Roh-Dokument, aus dem der Entwurf gelesen wurde — Grundlage jedes Patches. */
  doc: Record<string, unknown>;
  entwurf: SpecEntwurf;
}

export function ProcessSpecCard({ process }: { process: ProcessDefinitionView }) {
  const ctx = useUserContext();
  const darfLesen = darfVorgang(ctx.data, "my-process-content.read");
  const darfSchreiben = darfVorgang(ctx.data, "my-process-content.write");
  // Welche Repo-Liste? Ein Admin sieht die volle Whitelist, ein Prozess-Verantwortlicher
  // ohne Admin-Rechte bekommt darauf ein 403 und muss über die `:self`-Tür gehen. Solange
  // der Kontext lädt, wird GAR NICHT gefragt (sonst der falsche Scope für einen Wimpernschlag).
  const adminSicht = darfJetzt(ctx, "repositories.view");
  // Review L9: gar nicht erst fragen, wenn diese Person die Ablage nicht lesen darf.
  //
  // Review L8 (Kosten, bewusst so): das ist die volle Repo-Liste, nur um EINEN Eintrag zu
  // finden. Ein Einzel-Abruf `GET /git-content/repos/:id` existiert nicht, und die Liste ist
  // dieselbe Query wie in der Werkbank/den Fach-Sichten — der Treffer kommt in der Regel aus
  // dem Query-Cache. Erst wenn der Master eine Einzel-Auskunft anbietet, lohnt der Umbau.
  const darfAblageLesen = darfJetzt(ctx, "my-process-content.read");
  const repos = useManagedRepos(adminSicht ? undefined : "mine", { enabled: darfAblageLesen });

  const repoId = process.managedRepoId ?? `prc-${process.id}`;
  const repo = (repos.data ?? []).find((r) => r.id === repoId) ?? null;

  const [activeRef, setActiveRef] = useState(LIVE_REF);
  const [stand, setStand] = useState<Stand | null>(null);
  const [leseFehler, setLeseFehler] = useState<string | null>(null);
  const [schemaFunde, setSchemaFunde] = useState<Array<{ path: string; message: string }> | null>(null);

  const datei = useRepoFile(repo?.id, repo && darfAblageLesen ? SPEC_PFAD : null, apiRef(activeRef));
  // Der Katalog ist hier nur Auswahlhilfe — wer ihn nicht lesen darf, bekommt die Liste
  // gar nicht erst angefragt (und die Maske sagt, dass sie nicht abrufbar ist).
  const connectors = useConnectors({ enabled: darfAblageLesen && darfJetzt(ctx, "connectors.view") });
  const speichern = useSaveRepoFile();

  const inhalt = datei.data?.content ?? null;
  const sha = datei.data?.sha ?? null;
  const binaer = datei.data?.isBinary ?? false;

  useEffect(() => {
    if (!datei.data) return;
    if (binaer || inhalt === null) {
      setLeseFehler("Die Definition ist nicht als Text lesbar.");
      setStand(null);
      return;
    }
    const gelesen = leseSpecDokument(inhalt);
    if (!gelesen.ok) {
      setLeseFehler(gelesen.fehler);
      setStand(null);
      return;
    }
    setLeseFehler(null);
    setSchemaFunde(null);
    setStand({ ref: activeRef, sha: sha ?? "", doc: gelesen.doc, entwurf: leseEntwurf(gelesen.doc) });
    // Bewusst am INHALT (nicht am Query-Objekt): ein Hintergrund-Abruf mit unverändertem
    // Text lässt ungespeicherte Eingaben stehen; ein wirklich geänderter Stand (auch der
    // eigene, frisch gespeicherte) setzt den Entwurf auf die Server-Wahrheit zurück.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo?.id, activeRef, sha, inhalt, binaer]);

  // Nur, was zum GERADE gewählten Stand gehört, ist bedienbar (siehe `Stand.ref`).
  const standAktuell = stand && stand.ref === activeRef ? stand : null;

  const kontext = useMemo(() => (standAktuell ? leseKontext(standAktuell.doc) : null), [standAktuell]);

  // Nicht abrufbar ist NICHT dasselbe wie leer: eine Störung (oder eine fehlende
  // Leseberechtigung) darf keine korrekt deklarierten Systeme als unbekannt markieren.
  const katalogFehlt = connectors.isError || (connectors.data === undefined && !connectors.isFetching);

  const befunde: Befund[] = useMemo(() => {
    if (!standAktuell || !kontext) return [];
    const ids = katalogFehlt || !connectors.data ? null : connectors.data.map((c) => c.id);
    return pruefeEntwurf(standAktuell.entwurf, kontext, ids);
  }, [standAktuell, kontext, connectors.data, katalogFehlt]);

  const schreibGrund = writeBlockedReason(repo?.lifecycle, activeRef);
  const gesperrt = !darfSchreiben || schreibGrund !== null;

  const neuerInhalt = useMemo(() => {
    if (!standAktuell) return null;
    return serialisiereSpec(standAktuell.doc, baueAbschnitte(standAktuell.entwurf, standAktuell.doc));
  }, [standAktuell]);
  // Der Vergleichspunkt ist das NORMALISIERTE Original, nicht der Dateitext: sonst wäre
  // eine bloss anders eingerückte Datei schon eine „Änderung", und die Karte schriebe eine
  // Umformatierung, die niemand verlangt hat.
  const unveraendert = useMemo(() => {
    if (!standAktuell || neuerInhalt === null) return true;
    return neuerInhalt === serialisiereSpec(standAktuell.doc, {});
  }, [standAktuell, neuerInhalt]);

  const speicherbar = darfSpeichern({ gesperrt, fehler: hatFehler(befunde), unveraendert });

  if (!darfLesen) return null;

  const aendern = (patch: Partial<SpecEntwurf>): void => {
    setStand((alt) => (alt ? { ...alt, entwurf: { ...alt.entwurf, ...patch } } : alt));
  };

  /**
   * Review L4: der Stand-Wechsel verwirft ungespeicherte Eingaben (die neue Datei setzt den
   * Entwurf frisch). Vorher passierte das wortlos — jetzt wird gefragt. Kein eigener Dialog:
   * die Werkbank fragt an derselben Art Stelle genauso (`window.prompt`/`confirm`).
   */
  const standWechseln = (neu: string): void => {
    if (neu === activeRef) return;
    if (!unveraendert && typeof window.confirm === "function") {
      if (!window.confirm("Es gibt ungespeicherte Änderungen an der Definition. Beim Wechsel gehen sie verloren. Trotzdem wechseln?")) {
        return;
      }
    }
    setActiveRef(neu);
  };

  const abschicken = (): void => {
    if (!repo || !standAktuell || neuerInhalt === null) return;
    // Review L6: ZWEI Schlösser (Muster #324) — der gesperrte Knopf ist die Anzeige, dieser
    // `return` die Durchsetzung. BEIDE fragen dieselbe Regel, sonst laufen sie auseinander.
    if (!speicherbar) return;
    setSchemaFunde(null);
    speichern.mutate(
      {
        repoId: repo.id,
        path: SPEC_PFAD,
        content: neuerInhalt,
        message: "Prozess-Definition bearbeitet",
        sha: standAktuell.sha || undefined,
        ref: apiRef(activeRef),
      },
      {
        onSuccess: () => toast.success("Gespeichert — die Änderung steht im Entwurf."),
        onError: (err) => {
          const body = err instanceof ApiError ? (err.body as { code?: string; issues?: Array<{ path: string; message: string }> } | undefined) : undefined;
          if (body?.code === "ARTIFACT_SCHEMA_INVALID" && Array.isArray(body.issues)) {
            setSchemaFunde(body.issues);
            toast.error("Der Master hat die Definition abgelehnt — Fundstellen unten.");
            return;
          }
          toast.error(repoErrorMessage(err, "Speichern fehlgeschlagen."));
        },
      },
    );
  };

  const maskenProps =
    standAktuell && kontext ? { entwurf: standAktuell.entwurf, aendern, kontext, gesperrt, befunde } : null;

  return (
    <SectionCard title="Definition" noPad testId="process-spec-card">
      {repo && <LifecycleBar repo={repo} activeRef={activeRef} onRefChange={standWechseln} />}
      <div className="space-y-4 p-[18px]">
        {repos.isError ? (
          <p className="text-[12.5px] text-danger" data-testid="process-spec-repos-fehler">
            Die Ablage dieses Prozesses ist gerade nicht abrufbar — später erneut versuchen.
          </p>
        ) : !repo ? (
          <p className="text-[12.5px] text-text3">
            {repos.isLoading ? "Lade…" : "Für diesen Prozess ist keine bearbeitbare Ablage hinterlegt."}
          </p>
        ) : datei.isError ? (
          <p className="text-[12.5px] text-danger" data-testid="process-spec-datei-fehler">
            Die Definition konnte nicht gelesen werden
            {(datei.error as ApiError | undefined)?.status === 404 ? " — in diesem Stand gibt es keine." : "."}
          </p>
        ) : leseFehler ? (
          <p className="text-[12.5px] text-danger" data-testid="process-spec-lesefehler">
            {leseFehler}
          </p>
        ) : !maskenProps ? (
          <p className="text-[12.5px] text-text3">Lade…</p>
        ) : (
          <>
            {schreibGrund && (
              <p className="rounded-md border border-border bg-surface2 px-3 py-2 text-[12.5px] text-text2" data-testid="process-spec-schreibsperre">
                {schreibGrund} Zum Ändern oben einen Entwurf anlegen oder auswählen.
              </p>
            )}
            {!darfSchreiben && (
              <p className="text-[12.5px] text-text3">Nur lesend — für Änderungen fehlt die Berechtigung.</p>
            )}

            <AusloeserMaske {...maskenProps} />
            <InstanziierungsMaske {...maskenProps} />
            <SystemeMaske
              {...maskenProps}
              connectors={katalogFehlt ? null : (connectors.data ?? null)}
              connectorsFehler={katalogFehlt}
            />
            <EingabenMaske {...maskenProps} />
            <WeisungenMaske {...maskenProps} />
            <ReglerMaske {...maskenProps} />

            {schemaFunde && (
              <div className="rounded-md border border-danger-border bg-danger-bg px-3 py-2" data-testid="process-spec-schema-funde">
                <p className="text-[12.5px] font-medium">Der Master hat diese Stellen beanstandet:</p>
                <ul className="mt-1 list-disc pl-5 text-[12.5px]">
                  {schemaFunde.map((f, i) => (
                    <li key={i}>
                      {f.path ? `${f.path}: ` : ""}
                      {f.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-3 border-t border-border pt-4">
              <Button
                onClick={abschicken}
                disabled={!speicherbar || speichern.isPending}
                data-testid="process-spec-save"
              >
                <Save className="h-4 w-4" />
                {speichern.isPending ? "Speichere…" : "Speichern"}
              </Button>
              <span className="text-[12px] text-text3" data-testid="process-spec-status">
                {hatFehler(befunde)
                  ? "Erst die rot markierten Stellen beheben."
                  : unveraendert
                    ? "Keine Änderungen."
                    : "Änderungen sind noch nicht gespeichert."}
              </span>
            </div>
            <p className="text-[12px] leading-relaxed text-text3">
              Gespeichert wird in den oben gewählten Entwurf. Gültig wird die Definition erst
              mit der Freigabe und einer aktivierten Version — was diese Masken nicht anzeigen
              (Schritte, Vorgangsfelder, Rollen), bleibt beim Speichern unverändert stehen.
              {/* Review L1: der Wegweiser nur für die, die den Weg auch haben. Die Werkbank
                  hängt an `repositories.view`; ein Prozess-Verantwortlicher ohne dieses Recht
                  sähe sonst einen Verweis auf einen Knopf, den es für ihn nicht gibt. */}
              {adminSicht ? ` Diese Teile bearbeitest du über „Inhalt bearbeiten" am Kopf der Seite.` : ""}
            </p>
          </>
        )}
      </div>
    </SectionCard>
  );
}
